'use client';

import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import { faArrowLeft, faPencil, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { Typography, Button } from 'src/components';
import { useGetTeam } from 'src/hooks/teams';
import useUpdateTeam from 'src/hooks/teams/useUpdateTeam';
import { Switch, Tooltip } from 'src/components/PulseUI';
import { useUpdateTeamMutation } from 'src/api/redux/teams';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import { useAuth } from 'src/context/auth';
import { useGetAccountFolders } from 'src/hooks/accountFolders';
import TeamMembersSection from './TeamMembersSection';
import TeamFoldersSection from './TeamFoldersSection';

interface Team {
  id: number | string;
  name: string;
  description?: string;
  users?: any[];
  filter_set_folders?: any[];
  plays_enabled?: boolean;
}

interface TeamDetailViewProps {
  team: Team;
  onBack: () => void;
}

const TeamDetailView: React.FC<TeamDetailViewProps> = ({ team, onBack }) => {
  const { data: teamData, isLoading } = useGetTeam({
    id: team.id.toString(),
  });

  const { updateTeam, isUpdating } = useUpdateTeam();
  const { setNotification } = useNotification();
  const { data: accountFoldersData } = useGetAccountFolders();

  const { isSupportUser } = useAuthorization();
  const { refreshLocalUser } = useAuth();
  const log = useLogging();
  const [updateTeamPlays, { isLoading: isTogglingPlays }] =
    useUpdateTeamMutation();

  const handlePlaysToggle = async () => {
    try {
      await updateTeamPlays({
        id: team.id.toString(),
        plays_enabled: !teamData?.plays_enabled,
      }).unwrap();
      await refreshLocalUser();
      setNotification({
        title: 'Success',
        message: `Plays ${teamData?.plays_enabled ? 'disabled' : 'enabled'} for ${teamData?.name || team.name}`,
        type: 'success',
      });
    } catch (err) {
      log.exception(err, { tags: { context: 'team-plays-toggle' } });
      setNotification({
        title: 'Error',
        message: 'Failed to update plays setting',
        type: 'error',
      });
    }
  };

  const isEditable = teamData?.editable;

  // State for editing
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingMembers, setIsEditingMembers] = useState(false);
  const [isEditingFolders, setIsEditingFolders] = useState(false);
  const [formData, setFormData] = useState({
    name: team.name || '',
    description: team.description || '',
    users: [] as string[],
    filter_set_folders: [] as string[],
  });
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [initialFolders, setInitialFolders] = useState<string[]>([]);
  const [hasFolderChanges, setHasFolderChanges] = useState(false);
  const [errors, setErrors] = useState({
    name: '',
  });

  // Initialize form data and selected users when team data is loaded
  useEffect(() => {
    if (teamData) {
      setFormData({
        name: teamData.name || '',
        description: teamData.description || '',
        users: teamData.users?.map((user: any) => user.id.toString()) || [],
        filter_set_folders:
          teamData.filter_set_folders?.map((folder: any) =>
            folder.id.toString()
          ) || [],
      });

      if (teamData.users) {
        const userIds = teamData.users.map((user: any) => user.id.toString());
        setSelectedUsers(userIds);
      }

      if (teamData.filter_set_folders) {
        const folderIds = teamData.filter_set_folders.map((folder: any) =>
          folder.id.toString()
        );
        setSelectedFolders(folderIds);
        setInitialFolders(folderIds);
      }
    }
  }, [teamData]);

  // Check if folder selections have changed
  useEffect(() => {
    if (initialFolders.length > 0 || selectedFolders.length > 0) {
      const hasChanges =
        initialFolders.length !== selectedFolders.length ||
        initialFolders.some((id) => !selectedFolders.includes(id)) ||
        selectedFolders.some((id) => !initialFolders.includes(id));

      setHasFolderChanges(hasChanges);
    }
  }, [selectedFolders, initialFolders]);

  // Handle input changes for team details
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation errors when user types
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = { name: '' };
    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle saving team details
  const handleSaveTeamDetails = async () => {
    if (!validateForm()) return;

    try {
      const result = await updateTeam(team.id.toString(), {
        id: team.id.toString(),
        name: formData.name,
        description: formData.description,
        users: selectedUsers,
        filter_set_folders: selectedFolders,
      });

      if (result.success) {
        setNotification({
          title: 'Success',
          message: 'Team details updated successfully',
          type: 'success',
        });
        setIsEditing(false);
      } else {
        setNotification({
          title: 'Error',
          message: 'Failed to update team details',
          type: 'error',
        });
      }
    } catch (error) {
      setNotification({
        title: 'Error',
        message: 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  // Handle user selection changes
  const handleUserSelectionChange = (users: string[]) => {
    setSelectedUsers(users);
  };

  // Handle saving team members
  const handleSaveTeamMembers = async () => {
    try {
      const result = await updateTeam(team.id.toString(), {
        id: team.id.toString(),
        name: formData.name,
        description: formData.description,
        users: selectedUsers,
        filter_set_folders: selectedFolders,
      });

      if (result.success) {
        setNotification({
          title: 'Success',
          message: 'Team members updated successfully',
          type: 'success',
        });
        setIsEditingMembers(false);
      } else {
        setNotification({
          title: 'Error',
          message: 'Failed to update team members',
          type: 'error',
        });
      }
    } catch (error) {
      setNotification({
        title: 'Error',
        message: 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  // Handle folder toggle
  const handleFolderToggle = (folderId: string) => {
    setSelectedFolders((prev) => {
      if (prev.includes(folderId)) {
        return prev.filter((id) => id !== folderId);
      } else {
        return [...prev, folderId];
      }
    });
  };

  // Handle cancel folder changes
  const handleCancelFolderChanges = () => {
    setSelectedFolders([...initialFolders]);
    setIsEditingFolders(false);
    setHasFolderChanges(false);
  };

  // Handle save folder assignments
  const handleSaveFolderAssignments = async () => {
    try {
      const result = await updateTeam(team.id.toString(), {
        id: team.id.toString(),
        name: formData.name,
        description: formData.description,
        users: selectedUsers,
        filter_set_folders: selectedFolders,
      });

      if (result.success) {
        setNotification({
          title: 'Success',
          message: 'Folder assignments updated successfully',
          type: 'success',
        });
        setInitialFolders([...selectedFolders]);
        setIsEditingFolders(false);
        setHasFolderChanges(false);
      } else {
        setNotification({
          title: 'Error',
          message: 'Failed to update folder assignments',
          type: 'error',
        });
      }
    } catch (error) {
      setNotification({
        title: 'Error',
        message: 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Header with back button */}
      <div className="flex items-center mb-6">
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<Icon icon={faArrowLeft} />}
          onClick={onBack}
          className="mr-4"
          data-testid="back-to-teams-button"
        >
          Back to Teams
        </Button>
      </div>

      {/* Team Details Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Icon icon={faUserGroup} className="text-neutral-700" size="lg" />
            {isEditing ? (
              <div className="flex-1">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${
                    errors.name ? 'border-red-500' : 'border-neutral-300'
                  } rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary`}
                  placeholder="Enter team name"
                  data-testid="team-name-input"
                />
                {errors.name && (
                  <p
                    className="mt-1 text-sm text-red-600"
                    data-testid="team-name-error"
                  >
                    {errors.name}
                  </p>
                )}
              </div>
            ) : (
              <Typography variant="h4" className="text-primary">
                {teamData?.name || team.name}
              </Typography>
            )}
          </div>
          {!isEditing ? (
            isEditable ? (
              <Button
                variant="solid"
                color="neutral"
                size="sm"
                startDecorator={<Icon icon={faPencil} />}
                onClick={() => setIsEditing(true)}
                data-testid="edit-team-details-button"
              >
                Edit Details
              </Button>
            ) : null
          ) : (
            // Show Save/Cancel buttons only when in edit mode
            <div className="flex gap-2">
              <Button
                variant="outlined"
                color="neutral"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  // Reset form data
                  if (teamData) {
                    setFormData({
                      name: teamData.name || '',
                      description: teamData.description || '',
                      users:
                        teamData.users?.map((user: any) =>
                          user.id.toString()
                        ) || [],
                      filter_set_folders:
                        teamData.filter_set_folders?.map((folder: any) =>
                          folder.id.toString()
                        ) || [],
                    });
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                size="sm"
                onClick={handleSaveTeamDetails}
                disabled={isUpdating}
              >
                Save
              </Button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="mb-4">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter team description"
              data-testid="team-description-input"
            />
          </div>
        ) : (
          <Typography className="text-neutral-600 mb-4">
            {teamData?.description ||
              team.description ||
              'No description provided.'}
          </Typography>
        )}
      </div>

      {/* Plays Toggle */}
      <div className="flex items-center gap-3 mb-8">
        <Tooltip
          content={
            !isSupportUser ? 'Only support users can toggle plays' : undefined
          }
        >
          <span className="flex items-center">
            <Switch
              checked={!!teamData?.plays_enabled}
              onChange={handlePlaysToggle}
              disabled={!isSupportUser || isLoading || isTogglingPlays}
              aria-label="Toggle plays for this team"
              data-testid="team-plays-toggle"
            />
          </span>
        </Tooltip>
        <span className="text-sm font-medium text-neutral-700">Plays</span>
        {isTogglingPlays ? (
          <span className="flex items-center gap-1.5 text-sm text-neutral-500">
            <Icon icon={faSpinner} className="animate-spin text-xs" />
            Updating…
          </span>
        ) : (
          <span className="text-sm text-neutral-500">
            {teamData?.plays_enabled ? 'Enabled' : 'Disabled'}
          </span>
        )}
      </div>

      {/* Members and Folders Sections Container */}
      <div className="flex flex-col md:flex-row gap-6">
        <TeamMembersSection
          teamData={teamData}
          isLoading={isLoading}
          isEditingMembers={isEditingMembers}
          isUpdating={isUpdating}
          selectedUsers={selectedUsers}
          setIsEditingMembers={setIsEditingMembers}
          handleUserSelectionChange={handleUserSelectionChange}
          handleSaveTeamMembers={handleSaveTeamMembers}
          resetSelectedUsers={() => {
            if (teamData?.users) {
              const userIds = teamData.users.map((user: any) =>
                user.id.toString()
              );
              setSelectedUsers(userIds);
            }
          }}
          disableEditing={!isEditable}
        />

        <TeamFoldersSection
          accountFoldersData={accountFoldersData}
          isEditingFolders={isEditingFolders}
          isUpdating={isUpdating}
          selectedFolders={selectedFolders}
          hasFolderChanges={hasFolderChanges}
          setIsEditingFolders={setIsEditingFolders}
          handleFolderToggle={handleFolderToggle}
          handleCancelFolderChanges={handleCancelFolderChanges}
          handleSaveFolderAssignments={handleSaveFolderAssignments}
        />
      </div>
    </div>
  );
};

export default TeamDetailView;
